import { DEMO_ORGS } from "../../mock/organisations";
import type { SessionPort } from "../../mock/session-port";
import type { DemoLinks, DemoTools } from "../demo";
import { sha256 } from "../logic/tokens";
import { IDENTITY_POLICY } from "../policy";
import type { IdentityApi, StaffApi } from "../types";
import { createAccountApi } from "./account";
import { createAdminApi } from "./admin";
import { policiesOf } from "./audit";
import { createAuthApi, openSession } from "./auth";
import { createMockAuthKit } from "./authkit";
import {
  createCtx,
  findOrg,
  liveSession,
  requireOrg,
  roleIn,
  type MockIdentityOptions,
} from "./context";
import { DEMO_SHARE_PASSWORD, DEMO_TOKENS } from "./demo-content";
import { createOrgApi } from "./organisation";
import { createRequestApi } from "./requests";
import { seedIdentity } from "./seed";
import { createShareApi } from "./shares";
import { createStaffApi } from "./staff";

/**
 * The in-browser identity service: IdentityApi for pages, StaffApi for the staff console, a SessionPort for the
 * projects mock and demo tools (mock AuthKit, demo inbox, "end session now"). State persists in localStorage.
 */

export interface MockIdentity {
  identity: IdentityApi;
  staff: StaffApi;
  demo: DemoTools;
  port: SessionPort;
  /**
   * `?dev-session=<first name>` signs in as that seeded person with first-run done (`asha`: Owner of Northlight
   * Studio); `?dev-session=out` signs out. Synchronous, so the first render already has the session. Mock only.
   */
  startDevSession(key: string): boolean;
}

export interface CreateMockIdentityOptions extends MockIdentityOptions {
  demoOrg: { name: string; projectPrefix: string };
}

function demoLinks(): DemoLinks {
  return {
    invitations: [
      {
        label: "Member invitation to Northlight Studio (tara.menon@example.in)",
        href: `/invite/${DEMO_TOKENS.memberInvitation}`,
      },
      {
        label: "Guest invitation (ishaan.guest@example.in)",
        href: `/invite/${DEMO_TOKENS.guestInvitation}`,
      },
      { label: "Expired invitation", href: `/invite/${DEMO_TOKENS.expiredInvitation}` },
      { label: "Revoked invitation", href: `/invite/${DEMO_TOKENS.revokedInvitation}` },
    ],
    shares: [
      {
        label: "Bodakdev Residence: plan options (view and comment)",
        href: `/s/${DEMO_TOKENS.share}`,
      },
      {
        label: "Kochi Boutique Hotel: concept set (password, downloads on)",
        href: `/s/${DEMO_TOKENS.sharePassword}`,
        note: `Password: ${DEMO_SHARE_PASSWORD}`,
      },
      { label: "Expired link", href: `/s/${DEMO_TOKENS.shareExpired}` },
      { label: "Revoked link", href: `/s/${DEMO_TOKENS.shareRevoked}` },
    ],
    projects: [
      {
        label: "A confidential project (members outside its team request access)",
        href: "/p/gachibowli-tower",
      },
    ],
  };
}

export async function createMockIdentity(
  options: CreateMockIdentityOptions,
): Promise<MockIdentity> {
  const stored = options.persistence.load();
  const state =
    stored ?? (await seedIdentity({ now: options.now(), demoOrg: options.demoOrg, hash: sha256 }));
  const ctx = createCtx(options, state);
  if (stored === undefined) ctx.persist();

  options.persistence.watch(() => {
    ctx.state = options.persistence.load() ?? ctx.state;
    for (const listener of ctx.listeners) listener();
  });

  const identity: IdentityApi = {
    ...createAuthApi(ctx),
    ...createAccountApi(ctx),
    ...createOrgApi(ctx),
    ...createAdminApi(ctx),
    ...createRequestApi(ctx),
    ...createShareApi(ctx, options.demoOrg.name),
    subscribe(listener) {
      ctx.listeners.add(listener);
      return () => {
        ctx.listeners.delete(listener);
      };
    },
  };

  const port: SessionPort = {
    current() {
      const { user, org, role } = requireOrg(ctx);
      return {
        person: {
          id: user.id,
          name: user.name,
          initials: user.initials,
          colour: user.colour,
          title: user.title,
          email: user.email,
        },
        organisation: { id: org.id, name: org.name, projectPrefix: org.projectPrefix },
        role,
      };
    },
    people(organisationId) {
      return ctx.state.memberships
        .filter((m) => m.organisationId === organisationId)
        .flatMap((m) => {
          const user = ctx.state.users.find((u) => u.id === m.userId);
          return user === undefined
            ? []
            : [
                {
                  id: user.id,
                  name: user.name,
                  initials: user.initials,
                  colour: user.colour,
                  title: user.title,
                  email: user.email,
                },
              ];
        });
    },
    hasProjectGrant(projectId, userId) {
      return ctx.state.projectGrants.some((g) => g.projectId === projectId && g.userId === userId);
    },
    membersSeeAllProjects(organisationId) {
      const org = ctx.state.organisations.find((o) => o.id === organisationId);
      return org === undefined || policiesOf(org).memberProjectAccess !== "invite_only";
    },
  };

  const demo: DemoTools = {
    authkit: createMockAuthKit(ctx),
    async inbox() {
      await ctx.wait();
      return [...ctx.state.outbox]
        .sort((a, b) => b.sentAt - a.sentAt)
        .map((mail) => ({
          id: mail.id,
          to: mail.to,
          subject: mail.subject,
          sentAt: new Date(mail.sentAt).toISOString(),
          body: [...mail.body],
          link: { label: mail.linkLabel, href: mail.href },
        }));
    },
    links: demoLinks,
    expireSessionNow() {
      const live = liveSession(ctx);
      if (live === "none" || live === "expired") return;
      live.session.endedAt = ctx.now();
      live.session.endReason = "expired";
      ctx.commit();
    },
  };

  const startDevSession = (key: string): boolean => {
    if (key === "out") {
      delete ctx.state.cookie;
      ctx.persist();
      return true;
    }
    const user = ctx.state.users.find(
      (u) => u.id === `p-${key}` || u.name.split(" ")[0]?.toLowerCase() === key,
    );
    if (user === undefined) return false;
    user.termsVersion = IDENTITY_POLICY.termsVersion;
    user.termsAcceptedAt ??= ctx.now();
    user.ageConfirmedAt ??= ctx.now();
    user.workspaceChosen = true;
    for (const membership of ctx.state.memberships.filter(
      (m) => m.userId === user.id && m.role === "owner",
    )) {
      const org = findOrg(ctx, membership.organisationId);
      if (org !== undefined) org.setupComplete = true;
    }
    const home =
      roleIn(ctx, user.id, DEMO_ORGS.northlight) === undefined ? undefined : DEMO_ORGS.northlight;
    openSession(ctx, user, "passkey", home);
    ctx.persist();
    return true;
  };

  return { identity, staff: createStaffApi(ctx), demo, port, startDevSession };
}
